import type { z } from 'zod';

export class ConsoleApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'ConsoleApiError';
    this.status = status;
  }
}

export async function callConsoleApi<S extends z.ZodType>(
  path: string,
  schema: S,
  init: { readonly method?: string; readonly body?: unknown } = {},
): Promise<z.infer<S>> {
  const response = await send(path, init);
  return schema.parse(await response.json());
}

export async function sendToConsoleApi(
  path: string,
  init: { readonly method?: string; readonly body?: unknown } = {},
): Promise<void> {
  await send(path, init);
}

async function send(
  path: string,
  init: { readonly method?: string; readonly body?: unknown },
): Promise<Response> {
  const response = await fetch(path, {
    method: init.method ?? 'GET',
    credentials: 'same-origin',
    headers: init.body !== undefined ? { 'content-type': 'application/json' } : {},
    ...(init.body !== undefined ? { body: JSON.stringify(init.body) } : {}),
  });
  if (!response.ok) throw new ConsoleApiError(response.status, await errorMessage(response));
  return response;
}

async function errorMessage(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { error?: { message?: unknown } };
    if (typeof body.error?.message === 'string') return body.error.message;
  } catch {}
  return `The console API answered ${response.status}.`;
}
